
import { Component, Prop } from 'vue-property-decorator';
import { Mixins } from 'vue-property-decorator';
import { PromiserMixin, PromiserType } from 'vuex-modals';
import PersistentModal from '@/components/shared/modals/PersistentModal';
import Autocomplete from '../shared/Autocomplete.vue';
import { Representations } from '../shared/representation';
import {
  Network,
  NetworkSimple,
  CampusSimple,
  DHCPServer,
} from '@/api/interfaces';
import { deepClone } from '@/helpers';
import { repositories } from '@/api/ApiFactory';
import { Query } from '@/api/query';
import ErrorHandler from '@/components/shared/errorHandler';

type NetworkShrinked = Pick<Network, 'id' | 'name' | 'slug'>;

interface NetworkChanges {
  add: NetworkShrinked[];
  remove: NetworkShrinked[];
}

function getInitialDHCPServer(): DHCPServer {
  return {
    id: null,
    name: '',
    address: '',
    campus: null,
    networks: [],
  };
}

@Component({
  components: {
    Autocomplete,
  },
})
export default class DHCPServerModal extends Mixins<
  PromiserType,
  PersistentModal
>(PromiserMixin, PersistentModal) {
  @Prop({ default: null }) private campusSlug!: string | null;
  @Prop({ default: null }) private dhcpServer!: DHCPServer | null;
  @Prop({ default: null }) private fixedNetworkCampusSlug!: string | null;
  @Prop({ default: false }) private canChangeCampus!: boolean;

  private repr = new Representations();
  private dhcpServerClone: DHCPServer = getInitialDHCPServer();

  private loading = {
    initial: false,
    submit: false,
    networks: false,
  };

  private campus: CampusSimple | null = null;
  private networkCampus: CampusSimple | null = null;
  private checkedNetworks: NetworkShrinked[] = [];
  private campusNetworks: NetworkShrinked[] = [];
  private campusSearchApi = repositories.tenants.campus.searchCampuses;
  private networksChanges: { [key: string]: NetworkChanges } = {};
  private sumOfChanges: NetworkChanges = { add: [], remove: [] };

  private onCampusSelected() {
    this.networkCampus = this.campus;
    // reset networksChanges otherwise state of changes can be weird
    this.networksChanges = {};
    this.updateSumOfChanges();
    // manually fetch networks for the new campus because @input on our autocomplete doesn't get triggered
    this.getNetworksForCampus();
  }

  private get originallyCheckedNetworks(): NetworkShrinked[] {
    return (this.dhcpServerClone.networks as NetworkSimple[])
      .filter(
        (network: NetworkSimple) =>
          network.campus.id === this.networkCampus!.id,
      )
      .map((network: NetworkSimple) => {
        const clone: any = deepClone(network);
        delete clone.campus;
        return clone as NetworkShrinked;
      });
  }

  private getNetworksChangesByCampus(campusSlug: string) {
    if (
      Object.prototype.hasOwnProperty.call(this.networksChanges, campusSlug)
    ) {
      return this.networksChanges[campusSlug];
    }
    return {
      add: [] as NetworkShrinked[],
      remove: [] as NetworkShrinked[],
    };
  }

  private updateSumOfChanges() {
    const changes: NetworkChanges[] = Object.values(this.networksChanges);
    this.sumOfChanges = {
      add: changes.flatMap(
        (networkChange: NetworkChanges) => networkChange.add,
      ),
      remove: changes.flatMap(
        (networkChange: NetworkChanges) => networkChange.remove,
      ),
    };
  }

  private updateCheckedNetworks() {
    if (this.networkCampus === null) {
      this.checkedNetworks = [];
      return;
    }
    const networksChanges = this.getNetworksChangesByCampus(
      this.networkCampus!.slug,
    );
    this.checkedNetworks = [
      ...this.originallyCheckedNetworks.filter(
        (network: NetworkShrinked) =>
          !networksChanges.remove.some(
            (innerNetwork: NetworkShrinked) => innerNetwork.id === network.id,
          ),
      ),
      ...networksChanges.add,
    ];
  }

  private applyNetworkChanges() {
    const addToAdd = this.checkedNetworks.filter(
      (network: NetworkShrinked) =>
        !this.originallyCheckedNetworks.some(
          (innerNetwork: NetworkShrinked) => innerNetwork.id === network.id,
        ),
    );
    const removeFromAdd = this.campusNetworks.filter(
      (network: NetworkShrinked) =>
        !this.checkedNetworks.some(
          (innerNetwork: NetworkShrinked) => innerNetwork.id === network.id,
        ),
    );
    const addToRemove = this.originallyCheckedNetworks.filter(
      (network: NetworkShrinked) =>
        !this.checkedNetworks.some(
          (innerNetwork: NetworkShrinked) => innerNetwork.id === network.id,
        ),
    );
    const removeFromRemove = this.checkedNetworks;

    const campusNetworksChanges = this.getNetworksChangesByCampus(
      this.networkCampus!.slug,
    );
    const newAdd = Array.from(
      new Set([...campusNetworksChanges.add, ...addToAdd]),
    );
    campusNetworksChanges.add = newAdd.filter(
      (network: NetworkShrinked) =>
        !removeFromAdd.some(
          (innerNetwork: NetworkShrinked) => innerNetwork.id === network.id,
        ),
    );
    const newRemove = Array.from(
      new Set([...campusNetworksChanges.remove, ...addToRemove]),
    );
    campusNetworksChanges.remove = newRemove.filter(
      (network: NetworkShrinked) =>
        !removeFromRemove.some(
          (innerNetwork: NetworkShrinked) => innerNetwork.id === network.id,
        ),
    );
    // remove campus slug key from network changes if it was reverted to no changes
    if (
      campusNetworksChanges.add.length === 0 &&
      campusNetworksChanges.remove.length === 0
    ) {
      delete this.networksChanges[this.networkCampus!.slug];
      return;
    }
    this.networksChanges[this.networkCampus!.slug] = campusNetworksChanges;
    this.updateSumOfChanges();
  }

  private async getNetworksForCampus() {
    if (this.networkCampus === null) {
      this.campusNetworks = [];
      return;
    }
    this.loading.networks = true;
    const { data } = await repositories.lan.network.getNetworks(
      new Query({ campus_slug: this.networkCampus.slug }),
    );
    // convert from Network to NetworkShrinked
    const shrinkedNetworks = [];
    for (const network of data.results) {
      shrinkedNetworks.push({
        id: network.id,
        name: network.name,
        slug: network.slug,
      });
    }
    this.campusNetworks = shrinkedNetworks;
    this.updateCheckedNetworks();
    this.loading.networks = false;
  }

  private submit(): void {
    const data = this.dhcpServerClone;
    this.loading.submit = true;
    const networks = {
      add: this.sumOfChanges.add.map((network: NetworkShrinked) => network.id!),
      remove: this.sumOfChanges.remove.map(
        (network: NetworkShrinked) => network.id!,
      ),
    };
    const create = this.dhcpServerClone.id ? false : true;
    const api = create
      ? repositories.lan.dhcpServer.createDHCPServer({
          campusSlug: this.campus ? this.campus.slug : null,
          name: this.dhcpServerClone.name,
          address: this.dhcpServerClone.address,
          networks: networks.add,
        })
      : repositories.lan.dhcpServer.updateDHCPServer(
          this.dhcpServerClone.id as number,
          {
            campusSlug: this.campus ? this.campus.slug : null,
            name: this.dhcpServerClone.name,
            address: this.dhcpServerClone.address,
            networks,
          },
          this.$route.params.campusSlug || null,
        );
    api
      .then((res: any) => {
        this.ok(res.data);
      })
      .catch((error: any) => {
        const jobtext = create ? 'kreiranju' : 'posodabljanju';
        this.$toasted.error(
          new ErrorHandler({ error, status: true }, {}).toString(),
        );
        this.loading.submit = false;
      });
  }

  private async created() {
    this.loading.initial = true;
    if (this.dhcpServer != null) {
      // fetch dhcp server to get all of its networks
      try {
        const { data } = await repositories.lan.dhcpServer.getDHCPServer(
          this.dhcpServer.id!,
        );
        // remove management networks because we can't match with campuses + we don't need to fix their dhcp relations
        data.networks = (data.networks as NetworkSimple[]).filter(
          (network: NetworkSimple) => network.campus !== null,
        );
        this.dhcpServerClone = data;
      } catch (error) {
        this.$toasted.error(
          new ErrorHandler(
            { error, status: true },
            { itemMessageText: 'dhcp strežniku' },
          ).toString(),
        );
        this.loading.initial = false;
        return;
      }
    }
    try {
      if (this.campusSlug) {
        await this.fetchCampus(this.campusSlug);
      } else if (this.fixedNetworkCampusSlug) {
        await this.fetchNetworkCampus(this.fixedNetworkCampusSlug);
      }
      this.loading.initial = false;
    } catch (error) {
      this.$toasted.error('Neznana napaka');
      this.loading.initial = false;
    }
  }

  private async fetchCampus(campusSlug: string) {
    try {
      const { data } = await repositories.tenants.campus.getCampus(campusSlug);
      if (data) {
        this.campus = data;
        this.onCampusSelected();
      }
    } catch (error) {
      this.$toasted.error(
        new ErrorHandler(
          { error, status: true },
          { itemMessageText: 'kampusu' },
        ).toString(),
      );
    }
  }

  private async fetchNetworkCampus(campusSlug: string) {
    try {
      const { data } = await repositories.tenants.campus.getCampus(campusSlug);
      this.networkCampus = data;
      this.getNetworksForCampus();
    } catch (error) {
      this.$toasted.error(
        new ErrorHandler(
          { error, status: true },
          { itemMessageText: 'kampusu' },
        ).toString(),
      );
    }
  }
}
